import { Link, useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, Result, Spin, Typography, Button, List, Space, Image } from "antd";
import { LeftOutlined, RightOutlined, FileTextOutlined } from "@ant-design/icons";
import { useBlogDetail } from "../hooks/useBlogDetail";
import { useFormatDate } from "../hooks/useFormatDate";
import { useUser } from "../hooks/useUser";
import { supabase } from "../lib/supabase";
import { trackBlogPostViewed } from "../lib/posthog";
import { BlogPostMediaGallery } from "../components/blog/BlogPostMediaGallery";
import {
  getBlogMediaPublicUrl,
  normalizeBlogMedia,
} from "../utils/blogMediaUtils";

const { Title, Paragraph } = Typography;

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Renders body as HTML when it contains tags; otherwise linkifies plain URLs. Ensures <a> open in new tab. */
function prepareBodyHtml(body: string): string {
  if (!body) return "";
  if (body.includes("<")) return body;
  return body.split(/(https?:\/\/[^\s<>"']+)/g).map((p) =>
    /^https?:\/\//.test(p)
      ? `<a href="${escapeHtml(p)}" target="_blank" rel="noopener noreferrer">${escapeHtml(p)}</a>`
      : escapeHtml(p)
  ).join("");
}

export function BlogPostPage() {
  const { t } = useTranslation("translation");
  const formatDate = useFormatDate();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const { post, previous, next, otherPosts, loading, error } = useBlogDetail(id);
  const contentRef = useRef<HTMLDivElement>(null);
  const viewRecordedRef = useRef<string | null>(null);

  useEffect(() => {
    contentRef.current?.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  }, [post?.body, post?.excerpt]);

  // Record one view per page load (total); unique is derived as count(distinct user_id) in admin
  useEffect(() => {
    if (!post || !user?.id || viewRecordedRef.current === post.id) return;
    viewRecordedRef.current = post.id;
    trackBlogPostViewed(post.id, post.title);
    supabase
      .from("blog_post_views")
      .insert({ post_id: post.id, user_id: user.id })
      .then(({ error }) => {
        if (error) console.warn("Blog view record failed:", error.message);
      });
  }, [post?.id, post?.title, user?.id]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title={t("blog.postNotFound")}
          subTitle={error ?? t("blog.postNotFoundDescription")}
          extra={
            <Link to="/dashboard">
              <Button type="primary">{t("blog.backToDashboard")}</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const coverUrl = getBlogMediaPublicUrl(post.cover_image_path);

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <Card>
        <Link to="/dashboard" style={{ marginBottom: 16, display: "inline-block" }}>
          <Button type="text" icon={<LeftOutlined />}>
            {t("blog.backToDashboard")}
          </Button>
        </Link>

        <Title level={2} style={{ marginTop: 0 }}>
          {post.title}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {formatDate(post.created_at, "DD MMMM YYYY")}
        </Paragraph>

        {coverUrl && (
          <Image
            src={coverUrl}
            alt={post.title}
            style={{
              width: "100%",
              maxHeight: 420,
              objectFit: "cover",
              borderRadius: 4,
              marginBottom: 24,
            }}
          />
        )}

        <div ref={contentRef}>
          {post.excerpt && (
            <Paragraph style={{ fontStyle: "italic", color: "rgba(0,0,0,0.7)", marginBottom: 24 }}>
              <span dangerouslySetInnerHTML={{ __html: prepareBodyHtml(post.excerpt) }} />
            </Paragraph>
          )}

          <div
            className="blog-body"
            style={{ whiteSpace: post.body.includes("<") ? "normal" : "pre-wrap" }}
            dangerouslySetInnerHTML={{ __html: prepareBodyHtml(post.body) }}
          />
        </div>

        <BlogPostMediaGallery media={normalizeBlogMedia(post.media)} />

        <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #f0f0f0" }}>
          <Space size="middle" wrap>
            {previous ? (
              <Link to={`/blog/${previous.id}`}>
                <Button icon={<LeftOutlined />} title={previous.title}>
                  {t("blog.previousPost")}
                </Button>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link to={`/blog/${next.id}`}>
                <Button title={next.title}>
                  {t("blog.nextPost")} <RightOutlined />
                </Button>
              </Link>
            ) : null}
          </Space>
        </div>
      </Card>

      {otherPosts.length > 0 && (
        <Card
          title={
            <span>
              <FileTextOutlined style={{ marginRight: 8 }} />
              {t("blog.otherPosts")}
            </span>
          }
          style={{ marginTop: 24 }}
        >
          <List
            dataSource={otherPosts}
            renderItem={(p) => (
              <List.Item>
                <Link to={`/blog/${p.id}`} style={{ flex: 1 }}>
                  {p.title}
                </Link>
                <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 13 }}>
                  {formatDate(p.created_at)}
                </span>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
}
